import { defineStore } from "pinia";
import { ref, computed } from "vue";
import {
  getCompanyInfo,
  type IframeCompanyPicItem,
  type CompanyInfo,
} from "@/api/company";

export type BackgroundRegionId =
  | "hero-banner"
  | "kline-background"
  | "announcement-image"
  | "interaction-image"
  | "education-image";

export interface BackgroundConfig {
  id: BackgroundRegionId;
  defaultImage: string;
  customImage?: string;
}

const DEFAULT_BACKGROUNDS: Record<BackgroundRegionId, string> = {
  "hero-banner": "/src/assets/img/tzzgx.png",
  "kline-background": "/src/assets/img/stock.png",
  "announcement-image": "/src/assets/img/zszx.png",
  "interaction-image": "/src/assets/img/interaction-img.png",
  "education-image": "/src/assets/img/tdc.png",
};

// 数组下标到区域的映射（按返回顺序从上往下：索引0-4）
const INDEX_TO_REGION: Record<number, BackgroundRegionId> = {
  0: "kline-background", // 第1张（索引0）：股票信息背景
  1: "announcement-image", // 第2张（索引1）：公司公告
  2: "interaction-image", // 第3张（索引2）：投资者互动
  3: "education-image", // 第4张（索引3）：投资者教育
  4: "hero-banner", // 第5张（索引4）：hero banner（预留）
};

export const useBackgroundStore = defineStore("background", () => {
  // 是否启用编辑模式
  const enableEditMode = ref(false);

  // keyFlag 从路由获取
  const keyFlag = ref<string>("");

  // 背景图是否加载完成
  const imagesLoaded = ref(false);

  // 真实的背景图配置（从后端获取）
  const backgroundConfigs = ref<Partial<Record<BackgroundRegionId, string>>>(
    {}
  );

  // 从督导后台获取的图片配置
  const companyPicList = ref<IframeCompanyPicItem[]>([]);

  // 公司联系信息
  const companyContactInfo = ref<Partial<CompanyInfo>>({});

  // 公司名称
  const companyName = ref<string>("");

  // 股票代码
  const companyStockCodeLocal = ref<string>("");

  // 股票名称
  const stockName = ref<string>("");

  // 临时背景图（用于预览，不持久化）
  const temporaryBackgrounds = ref<Partial<Record<BackgroundRegionId, string>>>(
    {}
  );

  // 为每个区域提供单独的 computed，确保响应式
  const heroBannerImage = computed(() => {
    return (
      temporaryBackgrounds.value["hero-banner"] ??
      backgroundConfigs.value["hero-banner"] ??
      companyPicList.value[0]?.picPreviewUrl ??
      companyPicList.value[0]?.picUrl ??
      ""
    );
  });

  const klineBackgroundImage = computed(() => {
    return (
      temporaryBackgrounds.value["kline-background"] ??
      backgroundConfigs.value["kline-background"] ??
      companyPicList.value[1]?.picPreviewUrl ??
      companyPicList.value[1]?.picUrl ??
      ""
    );
  });

  const announcementImage = computed(() => {
    return (
      temporaryBackgrounds.value["announcement-image"] ??
      backgroundConfigs.value["announcement-image"] ??
      companyPicList.value[2]?.picPreviewUrl ??
      companyPicList.value[2]?.picUrl ??
      ""
    );
  });

  const interactionImage = computed(() => {
    return (
      temporaryBackgrounds.value["interaction-image"] ??
      backgroundConfigs.value["interaction-image"] ??
      companyPicList.value[3]?.picPreviewUrl ??
      companyPicList.value[3]?.picUrl ??
      ""
    );
  });

  const educationImage = computed(() => {
    return (
      temporaryBackgrounds.value["education-image"] ??
      backgroundConfigs.value["education-image"] ??
      companyPicList.value[4]?.picPreviewUrl ??
      companyPicList.value[4]?.picUrl ??
      ""
    );
  });

  // 公司联系信息
  const companyAddress = computed(() => companyContactInfo.value.address ?? "");
  const companyAddressThumbnail = computed(
    () => companyContactInfo.value.addressThumbnail ?? ""
  );
  const companyPhone = computed(
    () => companyContactInfo.value.phoneNumber ?? ""
  );
  const companyFax = computed(() => companyContactInfo.value.fax ?? "");
  const companyEmail = computed(() => companyContactInfo.value.email ?? "");
  const companyLat = computed(() => companyContactInfo.value.lat ?? "");
  const companyLng = computed(() => companyContactInfo.value.lng ?? "");
  const companyStockCode = computed(() => companyStockCodeLocal.value || companyContactInfo.value.stockCode || "");

  // 区域到数组下标的映射
  const REGION_TO_INDEX: Record<BackgroundRegionId, number> = {
    "hero-banner": 0, // 索引0: banner
    "kline-background": 1, // 索引1: 股票信息背景
    "announcement-image": 2, // 索引2: 公司公告
    "interaction-image": 3, // 索引3: 投资者互动
    "education-image": 4, // 索引4: 投资者教育
  };

  // 获取某个区域的显示图片
  function getDisplayImage(
    id: BackgroundRegionId,
    fallbackDefault: string
  ): string {
    const temp = temporaryBackgrounds.value[id];
    if (temp) return temp;

    const custom = backgroundConfigs.value[id];
    if (custom) return custom;

    // 从督导后台图片中查找（按数组索引）
    const index = REGION_TO_INDEX[id];
    const pic = companyPicList.value[index];
    if (pic) {
      return pic.picPreviewUrl || pic.picUrl;
    }

    return fallbackDefault;
  }

  // 从加载公司图片信息
  async function loadCompanyInfoImages(ak: string) {
    try {
      imagesLoaded.value = false;
      console.log(ak);
      const info = await getCompanyInfo(ak);
      console.log("[loadCompanyInfoImages] info:", info);
      if (info.iframeCompanyPicList) {
        companyPicList.value = info.iframeCompanyPicList;
      }
      // 保存公司联系信息
      companyContactInfo.value = {
        address: info.address,
        addressThumbnail: info.addressThumbnail,
        phoneNumber: info.phoneNumber,
        fax: info.fax,
        email: info.email,
        lat: info.lat,
        lng: info.lng,
        stockCode: info.stockCode,
        stockName: info.stockName,
      };
      // 保存股票代码
      const code = info.stockCode || (info as any).stockcode || (info as any).stock_code;
      if (code) {
        companyStockCodeLocal.value = code;
        console.log("[loadCompanyInfoImages] 设置 stockCode:", code);
      }
      // 保存股票名称
      const name = info.stockName || (info as any).stockname || (info as any).stock_name;
      if (name) {
        stockName.value = name;
        companyName.value = name;
        console.log("[loadCompanyInfoImages] 设置 stockName:", name);
      }
    } catch (error) {
      console.error("加载督导后台图片失败:", error);
    } finally {
      imagesLoaded.value = true;
    }
  }

  // 设置临时背景图
  function setTemporaryBackground(id: BackgroundRegionId, imageUrl: string) {
    temporaryBackgrounds.value = {
      ...temporaryBackgrounds.value,
      [id]: imageUrl,
    };
  }

  // 清除临时背景图
  function clearTemporaryBackground(id: BackgroundRegionId) {
    const newTemps = { ...temporaryBackgrounds.value };
    delete newTemps[id];
    temporaryBackgrounds.value = newTemps;
  }

  // 清除所有临时背景图
  function clearAllTemporaryBackgrounds() {
    temporaryBackgrounds.value = {};
  }

  // 从后端刷新背景图配置
  async function refreshBackgrounds() {
    backgroundConfigs.value = {};
  }

  // 启用编辑模式
  function setEditMode(enabled: boolean) {
    enableEditMode.value = enabled;
  }

  // 设置 keyFlag
  function setKeyFlag(flag: string) {
    keyFlag.value = flag;
  }

  // 设置公司名称
  function setCompanyName(name: string) {
    companyName.value = name;
  }

  // 设置股票代码
  function setCompanyStockCode(code: string) {
    companyStockCodeLocal.value = code;
  }

  return {
    enableEditMode,
    keyFlag,
    imagesLoaded,
    backgroundConfigs,
    companyPicList,
    companyContactInfo,
    companyName,
    companyStockCodeLocal,
    stockName,
    temporaryBackgrounds,
    heroBannerImage,
    klineBackgroundImage,
    announcementImage,
    interactionImage,
    educationImage,
    companyAddress,
    companyAddressThumbnail,
    companyPhone,
    companyFax,
    companyEmail,
    companyLat,
    companyLng,
    companyStockCode,
    setCompanyName,
    setCompanyStockCode,
    DEFAULT_BACKGROUNDS,
    getDisplayImage,
    loadCompanyInfoImages,
    setTemporaryBackground,
    clearTemporaryBackground,
    clearAllTemporaryBackgrounds,
    refreshBackgrounds,
    setEditMode,
    setKeyFlag,
  };
});
